import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import commitmentScannerService from "../../../services/commitmentScannerService";
import { ResultDetailModal } from "./ResultDetailModal";
import { PickProfileModal } from "./PickProfileModal";
import {
  STATUS_BADGE,
  MATCH_LABEL,
  TRUSTED_MATCHES,
  CHANGE_LABEL,
  playerName,
  profileNow,
  errorText,
} from "./scannerLabels";

const POLL_MS = 5000;

const FILTERS = [
  { key: "changes", label: "Changes to review", test: (r) => ["new", "school", "decommit"].includes(r.change) && r.review.state === "open" },
  { key: "committed", label: "Committed", test: (r) => r.status === "Committed" },
  { key: "review", label: "Needs Review", test: (r) => r.status === "Needs Review" },
  { key: "uncommitted", label: "Likely Uncommitted", test: (r) => r.status === "Likely Uncommitted" },
  { key: "unmatched", label: "No profile match", test: (r) => !r.player },
  { key: "all", label: "All", test: () => true },
];

const isNewTrusted = (r) =>
  r.change === "new" && r.review.state === "open" && TRUSTED_MATCHES.includes(r.matchMethod);

const formatDate = (d) => (d ? new Date(d).toLocaleString() : "");

export const CommitmentScannerPage = () => {
  const [status, setStatus] = useState(null);
  const [scans, setScans] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null); // { scan, results }
  const [filter, setFilter] = useState("changes");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [includeCommitted, setIncludeCommitted] = useState(false);
  const [openResult, setOpenResult] = useState(null);
  const [pickFor, setPickFor] = useState(null);
  const fileRef = useRef(null);

  const loadOverview = useCallback(async () => {
    const [s, list] = await Promise.all([commitmentScannerService.getStatus(), commitmentScannerService.listScans()]);
    setStatus(s);
    setScans(list.scans || []);
    return list.scans || [];
  }, []);

  const loadScan = useCallback(async (id) => {
    const data = await commitmentScannerService.getScan(id);
    setDetail(data);
    return data;
  }, []);

  useEffect(() => {
    loadOverview()
      .then((list) => list[0] && setSelectedId(list[0]._id))
      .catch((err) => setError(errorText(err)));
  }, [loadOverview]);

  useEffect(() => {
    if (!selectedId) return;
    loadScan(selectedId).catch((err) => setError(errorText(err)));
  }, [selectedId, loadScan]);

  // Refresh while the selected scan is still running
  const running = detail && ["queued", "running"].includes(detail.scan.status);
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(() => {
      loadScan(selectedId).catch(() => {});
      loadOverview().catch(() => {});
    }, POLL_MS);
    return () => clearInterval(t);
  }, [running, selectedId, loadScan, loadOverview]);

  const run = async (fn, okMessage) => {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const out = await fn();
      if (okMessage) setNotice(typeof okMessage === "function" ? okMessage(out) : okMessage);
      return out;
    } catch (err) {
      setError(errorText(err));
      return null;
    } finally {
      setBusy(false);
    }
  };

  const startScan = async (starter) => {
    const out = await run(starter);
    if (!out?.scan) return;
    await loadOverview();
    setFilter("all");
    setSelectedId(out.scan._id);
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) await startScan(() => commitmentScannerService.uploadScan(file));
  };

  const replaceResult = (updated) => {
    setDetail((d) => d && { ...d, results: d.results.map((r) => (r._id === updated._id ? updated : r)) });
    setOpenResult((o) => (o && o._id === updated._id ? updated : o));
  };

  const applyResult = async (r, body) => {
    const out = await run(() => commitmentScannerService.applyResult(r._id, body), "Profile updated.");
    if (out?.result) {
      replaceResult(out.result);
      setOpenResult(null);
    }
  };

  const dismissResult = async (r) => {
    const out = await run(() => commitmentScannerService.dismissResult(r._id));
    if (out?.result) {
      replaceResult(out.result);
      setOpenResult(null);
    }
  };

  const pickProfile = async (playerId) => {
    const out = await run(() => commitmentScannerService.setResultPlayer(pickFor._id, playerId));
    if (out?.result) {
      replaceResult(out.result);
      setPickFor(null);
    }
  };

  const results = useMemo(() => detail?.results || [], [detail]);
  const newTrusted = results.filter(isNewTrusted).length;
  const activeFilter = FILTERS.find((f) => f.key === filter) || FILTERS[0];
  const shown = results.filter(activeFilter.test);

  const applyAllNew = async () => {
    if (!window.confirm(`Mark ${newTrusted} player(s) as committed on their profiles? This shows on the live site.`)) return;
    const out = await run(() => commitmentScannerService.applyNew(selectedId), (o) => `${o.applied} profile(s) updated.`);
    if (out) await loadScan(selectedId);
  };

  const scan = detail?.scan;
  const pct = scan?.playerCount ? Math.round((scan.processedCount / scan.playerCount) * 100) : 0;
  const scanRunning = Boolean(status?.running);

  return (
    <div className="rounded-3xl bg-jp-card border border-jp-line shadow-sm p-6 space-y-6 text-jp-ink">
      <div>
        <h2 className="text-jp-accent-ink text-sm font-bold uppercase tracking-widest">COMMITMENT SCANNER</h2>
        <p className="text-xs text-jp-muted mt-1 max-w-3xl">
          Reads players' X accounts and uses Claude to spot commitments to four-year schools, then matches each
          player to their JUCO Pipeline profile. Nothing changes on a profile until you approve it.
        </p>
      </div>

      {error && (
        <div className="p-3 bg-red-900/20 border border-red-500 rounded-lg text-red-600 dark:text-red-300 text-sm">{error}</div>
      )}
      {notice && (
        <div className="p-3 bg-emerald-500/10 border border-emerald-600 rounded-lg text-emerald-700 dark:text-emerald-300 text-sm">{notice}</div>
      )}
      {status && !status.configured && (
        <div className="p-3 bg-amber-500/10 border border-amber-600 rounded-lg text-amber-700 dark:text-amber-200 text-sm">
          The scanner isn't switched on for this server yet: it needs an X API key and a Claude API key added to
          the server settings. You can look around, but scans can't start.
        </div>
      )}

      {/* Start a scan */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="rounded-2xl bg-jp-section border border-jp-line p-4 space-y-3">
          <h3 className="text-jp-ink font-medium">Scan players on JUCO Pipeline</h3>
          <p className="text-xs text-jp-muted">
            {status
              ? includeCommitted
                ? `${status.playersWithX} players have an X link on their profile.`
                : `${status.uncommittedWithX} uncommitted players have an X link on their profile.`
              : "Loading…"}
          </p>
          <label className="flex items-center gap-2 text-xs text-jp-muted">
            <input type="checkbox" checked={includeCommitted} onChange={(e) => setIncludeCommitted(e.target.checked)} />
            Also re-check players already marked committed
          </label>
          <button
            disabled={busy || !status?.configured || scanRunning}
            onClick={() => startScan(() => commitmentScannerService.databaseScan({ includeCommitted }))}
            className="px-4 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-500 disabled:opacity-40 text-white text-sm font-medium"
          >
            Scan these players
          </button>
        </div>

        <div className="rounded-2xl bg-jp-section border border-jp-line p-4 space-y-3">
          <h3 className="text-jp-ink font-medium">Upload a list</h3>
          <p className="text-xs text-jp-muted">
            Excel (.xlsx) with player names linked to their X profiles, or columns for name, school and X handle.
            Each player is matched to their profile automatically.
          </p>
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={onUpload} className="hidden" />
          <button
            disabled={busy || !status?.configured || scanRunning}
            onClick={() => fileRef.current?.click()}
            className="px-4 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-500 disabled:opacity-40 text-white text-sm font-medium"
          >
            Choose file and scan
          </button>
        </div>
      </div>
      {scanRunning && (
        <p className="text-xs text-jp-muted">A scan is running — new scans can start when it finishes.</p>
      )}

      {/* Past scans */}
      {scans.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-xs uppercase tracking-wide text-jp-muted">Scans</h3>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {scans.map((s) => (
              <button
                key={s._id}
                onClick={() => setSelectedId(s._id)}
                className={`shrink-0 text-left px-3 py-2 rounded-lg border text-xs ${
                  s._id === selectedId
                    ? "border-cyan-500/60 bg-cyan-500/10 text-jp-ink"
                    : "border-jp-line bg-jp-section hover:bg-jp-line"
                }`}
              >
                <div className="font-medium truncate max-w-[14rem]">{s.name}</div>
                <div className="text-jp-muted">
                  {formatDate(s.createdAt)} · {s.status === "done" ? `${s.committedCount} committed` : s.status}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Selected scan */}
      {scan && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm">
              <span className="text-jp-ink font-medium">{scan.name}</span>
              <span className="text-jp-muted">
                {" "}· {scan.processedCount}/{scan.playerCount} scanned · {scan.matchedCount} matched to a profile
              </span>
            </div>
            <div className="flex gap-2">
              {running && (
                <button
                  disabled={busy}
                  onClick={async () => {
                    await run(() => commitmentScannerService.cancelScan(scan._id), "Scan stopped.");
                    await Promise.all([loadScan(scan._id), loadOverview()]);
                  }}
                  className="px-3 py-1.5 rounded-lg bg-jp-section hover:bg-jp-line text-xs"
                >
                  Stop scan
                </button>
              )}
              {newTrusted > 0 && (
                <button
                  disabled={busy}
                  onClick={applyAllNew}
                  className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white text-xs font-medium"
                >
                  Approve {newTrusted} new commitment{newTrusted === 1 ? "" : "s"}
                </button>
              )}
            </div>
          </div>

          {running && (
            <div className="h-2 rounded-full bg-jp-section overflow-hidden">
              <div className="h-full bg-cyan-500 transition-all" style={{ width: `${pct}%` }} />
            </div>
          )}
          {scan.status === "error" && <p className="text-red-600 dark:text-red-300 text-xs">The scan stopped with an error: {scan.error}</p>}

          <div className="flex flex-wrap gap-2">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`px-3 py-1 rounded-full border text-xs ${
                  f.key === filter ? "border-cyan-500/60 bg-cyan-500/10 text-jp-ink" : "border-jp-line text-jp-muted hover:bg-jp-section"
                }`}
              >
                {f.label} ({results.filter(f.test).length})
              </button>
            ))}
          </div>

          <div className="overflow-x-auto rounded-xl border border-jp-line">
            <table className="w-full text-xs">
              <thead className="bg-jp-section text-jp-muted uppercase tracking-wide">
                <tr>
                  <th className="text-left px-3 py-2">Player on X</th>
                  <th className="text-left px-3 py-2">JUCO Pipeline profile</th>
                  <th className="text-left px-3 py-2">Scan says</th>
                  <th className="text-left px-3 py-2">Profile says</th>
                  <th className="text-left px-3 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-jp-line">
                {shown.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-3 py-6 text-center text-jp-muted">
                      {running && filter !== "all" ? "Nothing here yet — the scan is still going." : "Nothing in this list."}
                    </td>
                  </tr>
                )}
                {shown.map((r) => (
                  <tr key={r._id} className="hover:bg-jp-section">
                    <td className="px-3 py-2">
                      <div className="text-jp-ink">{r.playerName || "—"}</div>
                      <a
                        href={`https://x.com/${r.twitterUsername}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sky-600 dark:text-sky-300 hover:text-jp-accent"
                      >
                        @{r.twitterUsername}
                      </a>
                    </td>
                    <td className="px-3 py-2">
                      {r.player ? (
                        <>
                          <Link to={`/admin/player/${r.player._id}`} className="text-sky-600 dark:text-sky-300 hover:text-jp-accent">
                            {playerName(r.player)}
                          </Link>
                          <div className="text-jp-muted">
                            {r.player.team?.name ? `${r.player.team.name} · ` : ""}
                            {MATCH_LABEL[r.matchMethod]}
                          </div>
                        </>
                      ) : (
                        <button onClick={() => setPickFor(r)} className="text-amber-700 dark:text-amber-300 hover:text-amber-700 dark:hover:text-amber-200">
                          {MATCH_LABEL[r.matchMethod]} — pick profile
                        </button>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {r.scanState === "pending" ? (
                        <span className="text-jp-muted">Waiting…</span>
                      ) : (
                        <>
                          <span className={`px-2 py-0.5 rounded-full border ${STATUS_BADGE[r.status] || ""}`}>{r.status}</span>
                          {r.committedSchool && <div className="mt-1 text-jp-ink">{r.committedSchool}</div>}
                          {r.confidence > 0 && <div className="text-jp-muted">{r.confidence}% sure</div>}
                        </>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div>{profileNow(r.player)}</div>
                      {r.change && r.change !== "same" && (
                        <div className={r.change === "review" ? "text-jp-muted" : "text-amber-700 dark:text-amber-300"}>{CHANGE_LABEL[r.change]}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {r.review.state === "applied" && <span className="text-emerald-700 dark:text-emerald-300 mr-2">✓ Applied</span>}
                      {r.review.state === "dismissed" && <span className="text-jp-muted mr-2">Dismissed</span>}
                      {r.scanState === "done" && (
                        <button onClick={() => setOpenResult(r)} className="text-sky-600 dark:text-sky-300 hover:text-jp-accent">
                          {r.review.state === "open" ? "Review" : "Details"}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {openResult && (
        <ResultDetailModal
          result={openResult}
          busy={busy}
          onClose={() => setOpenResult(null)}
          onApply={(body) => applyResult(openResult, body)}
          onDismiss={() => dismissResult(openResult)}
          onPickProfile={() => {
            setPickFor(openResult);
            setOpenResult(null);
          }}
        />
      )}
      {pickFor && <PickProfileModal result={pickFor} busy={busy} onClose={() => setPickFor(null)} onPick={pickProfile} />}
    </div>
  );
};
